// SYNC con taxticbackend/src/utils/buildProposalHTMLv2.js.

import { formatRut, isRutLike, RutFormat } from "@fdograph/rut-utilities";
import { formatValorPlan } from "./formatNumber.js";
import { formatearMonedaCliente } from "./formatearMonedaCliente";
import { BRAND } from "../theme/brandTokens.js";
import { INTER_FONT_FACE_CSS } from "./interFontBase64.js";
import {
  modalidadItem,
  MODALIDAD_META,
  ORDEN_MODALIDAD,
  claveEmpresaFase,
  esCobroPorTrabajador,
} from "./propuestaItemLabels.js";
import {
  LOGO_PRINCIPAL_BLANCO_SRC,
  LOGO_ISOLOGO_NEGRO_SRC,
} from "./logoSources.js";
export {
  LOGO_PRINCIPAL_BLANCO_SRC,
  LOGO_ISOLOGO_NEGRO_SRC,
  INTER_FONT_FACE_CSS,
};

const _mvNumPage = parseFloat(BRAND.page.margin.split(" ")[0]);
const _mhPage = BRAND.page.margin.split(" ")[1];
const _hdrNumPage = parseFloat(BRAND.page.headerH);
const _ftrNumPage = parseFloat(BRAND.page.footerH);
export const PDF_PAGE_GEOMETRY = {
  marginTop: `${_mvNumPage + _hdrNumPage}mm`,
  marginRight: _mhPage,
  marginBottom: `${_mvNumPage + _ftrNumPage}mm`,
  marginLeft: _mhPage,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Escapa caracteres HTML en contenido generado por el usuario. */
export function esc(str) {
  if (str === null || str === undefined) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const _MESES_ABREV = [
  "ENE",
  "FEB",
  "MAR",
  "ABR",
  "MAY",
  "JUN",
  "JUL",
  "AGO",
  "SEP",
  "OCT",
  "NOV",
  "DIC",
];

/** Formato corto uppercase para portada: "27 ABR 2026". */
function formatFechaPortada(fechaStr) {
  let d;
  if (fechaStr) {
    const parts = String(fechaStr)
      .split(/[-/]/)
      .map((p) => parseInt(p, 10));
    if (parts.length >= 3 && !parts.some(Number.isNaN)) {
      d = new Date(parts[0], parts[1] - 1, parts[2]);
    }
  }
  if (!d || isNaN(d.getTime())) d = new Date();
  return `${d.getDate()} ${_MESES_ABREV[d.getMonth()]} ${d.getFullYear()}`;
}

/** Año "YYYY" desde fecha ISO o slash-separada; año actual si falta. */
function obtenerAnio(fechaStr) {
  if (fechaStr) {
    const [y] = String(fechaStr).split(/[-/]/);
    const yNum = parseInt(y, 10);
    if (!Number.isNaN(yNum)) return String(yNum);
  }
  return String(new Date().getFullYear());
}

/**
 * Divide el título de portada en dos líneas para renderizado bicolor.
 * Si contiene "\n" lo respeta. Si no, parte por la mitad de palabras.
 * Si solo hay una palabra, la 2da línea queda vacía y la 1ra absorbe todo.
 */
function splitTituloBicolor(titulo) {
  if (!titulo) return { linea1: "", linea2: "" };
  if (titulo.includes("\n")) {
    const [l1, ...rest] = titulo.split("\n");
    return { linea1: l1.trim(), linea2: rest.join(" ").trim() };
  }
  const palabras = titulo.trim().split(/\s+/);
  if (palabras.length <= 1) return { linea1: titulo.trim(), linea2: "" };
  const corte = Math.ceil(palabras.length / 2);
  return {
    linea1: palabras.slice(0, corte).join(" "),
    linea2: palabras.slice(corte).join(" "),
  };
}

/** Pad a 2 dígitos: 1 -> "01", 12 -> "12". */
function padNumero(n) {
  return String(n).padStart(2, "0");
}

/**
 * Pasos por defecto del flujo de aceptación cuando `propuesta.proximosPasos`
 * está vacío. Se renderizan en la contraportada y coinciden con el flujo real:
 * portal -> aceptación parcial por ítem -> OTP por email.
 */
const PASOS_ACEPTACION_DEFAULT = [
  {
    titulo: "Acceda al portal",
    descripcion: "Use el enlace recibido por correo.",
  },
  {
    titulo: "Revise y seleccione",
    descripcion: "Marque los servicios que desea contratar.",
  },
  {
    titulo: "Firme con el código",
    descripcion: "Confirme con el código que enviaremos a su correo.",
  },
];

/**
 * Parsea `propuesta.proximosPasos` (texto libre) en máximo 5 bloques
 * `{ titulo, descripcion }`. Cada bloque se separa por línea en blanco
 * (`\n\n`); la primera línea del bloque es el título y el resto la
 * descripción. Devuelve `[]` si el campo está vacío.
 */
function parseProximosPasos(texto) {
  if (!texto || !texto.trim()) {
    return { estructurado: false, bloques: [], textoBruto: "" };
  }
  const textoBruto = texto.trim();
  const bloquesRaw = textoBruto
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);
  // Estructurado si hay 2+ bloques separados por linea en blanco (formato canonico).
  // Si hay 1 solo bloque (vendedor escribio prosa o pego texto sin separar),
  // caer al render de prosa simple para no romper visualmente.
  if (bloquesRaw.length < 2) {
    return { estructurado: false, bloques: [], textoBruto };
  }
  const bloques = bloquesRaw
    .slice(0, 5)
    .map((bloque) => {
      const lineas = bloque
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      return {
        titulo: lineas[0] || "",
        descripcion: lineas.slice(1).join(" ") || "",
      };
    })
    .filter((b) => b.titulo);
  if (bloques.length < 2) {
    return { estructurado: false, bloques: [], textoBruto };
  }
  return { estructurado: true, bloques, textoBruto };
}

/**
 * Separa monto y código de moneda del output de `formatValorPlan({estilo:"v2"})`.
 * "$100.000 CLP" -> { monto: "$100.000", moneda: "CLP" }
 * "100,5 UF"     -> { monto: "100,5",    moneda: "UF" }
 */
function splitMontoMoneda(textoV2) {
  if (!textoV2) return { monto: "", moneda: "" };
  const i = textoV2.lastIndexOf(" ");
  if (i < 0) return { monto: textoV2, moneda: "" };
  return {
    monto: textoV2.slice(0, i).trim(),
    moneda: textoV2.slice(i + 1).trim(),
  };
}

/**
 * Convierte texto plano con `\n\n` (separador de párrafo) y `\n` (salto suave)
 * a HTML semántico de párrafos. Cada párrafo arranca como `<p class="seccion-parrafo">`.
 */
function renderParrafosV2(texto) {
  if (!texto) return "";
  return texto
    .split(/\n{2,}/)
    .map((parrafo) => parrafo.trim())
    .filter(Boolean)
    .map(
      (parrafo) =>
        `<p class="seccion-parrafo">${esc(parrafo).replace(/\n/g, "<br>")}</p>`,
    )
    .join("");
}

/**
 * Renderiza una lista ordenada de bloques `{ titulo, descripcion }` con
 * numeración grande a la izquierda y texto a la derecha.
 */
function renderProximosPasos(pasos) {
  return `
    <ol class="proximos-pasos-lista">
      ${pasos
        .map(
          (paso, i) => `
        <li class="proximos-pasos-item">
          <div class="proximos-pasos-numero">${esc(padNumero(i + 1))}</div>
          <div class="proximos-pasos-cuerpo">
            <div class="proximos-pasos-titulo">${esc(paso.titulo)}</div>
            ${paso.descripcion ? `<div class="proximos-pasos-descripcion">${esc(paso.descripcion)}</div>` : ""}
          </div>
        </li>
      `,
        )
        .join("")}
    </ol>
  `;
}

function renderProximosPasosProsa(textoBruto) {
  return renderParrafosV2(textoBruto);
}

/**
 * Renderiza una sección interna: eyebrow numerado uppercase + título con
 * cuadrado ícono + línea divisoria + body. El wrapper aplica
 * `break-before: page` para que cada sección arranque en página propia.
 */
function renderSeccionInternaV2({ numero, titulo, body }) {
  const tituloUpper = titulo.toUpperCase();
  return `
    <section class="seccion-interna">
      <div class="seccion-eyebrow">${esc(padNumero(numero))} · ${esc(tituloUpper)}</div>
      <div class="seccion-titulo-row">
        <span class="seccion-icono"></span>
        <h2 class="seccion-titulo">${esc(titulo)}</h2>
      </div>
      <hr class="seccion-divider" />
      <div class="seccion-body">${body}</div>
    </section>
  `;
}

// ─── CSS ──────────────────────────────────────────────────────────────────────

const CSS = `
  ${INTER_FONT_FACE_CSS}

  @page {
    size: ${BRAND.page.sizeA4};
    margin: ${PDF_PAGE_GEOMETRY.marginTop} ${PDF_PAGE_GEOMETRY.marginRight} ${PDF_PAGE_GEOMETRY.marginBottom} ${PDF_PAGE_GEOMETRY.marginLeft};
  }
  @page :first { margin: 0; }
  @page :last  { margin: 0; }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  body {
    font-family: ${BRAND.font.family.base};
    font-size: ${BRAND.font.size.body};
    color: ${BRAND.color.inkSoft};
    line-height: ${BRAND.font.lineHeight.body};
    background: ${BRAND.color.paper};
  }

  .page-break { break-before: page; }
  .no-break   { break-inside: avoid; }

  /* ── Portada — fondo negro full-bleed + cuadrado naranja superior derecha ── */
  .portada {
    position: relative;
    width: 210mm;
    height: 297mm;
    background: ${BRAND.color.inkSoft};
    color: ${BRAND.color.paper};
    overflow: hidden;
    page-break-after: always;
  }
  .portada-cuadrado {
    position: absolute;
    top: 0;
    right: 0;
    width: 55%;
    height: 55%;
    background: ${BRAND.color.primary};
  }
  .portada-logo {
    position: absolute;
    top: 22mm;
    left: 22mm;
    width: 50mm;
    height: auto;
    z-index: 1;
  }
  .portada-bloque-inferior {
    position: absolute;
    left: 22mm;
    right: 22mm;
    bottom: 28mm;
  }
  .portada-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    margin-bottom: 14mm;
  }
  .portada-titulo {
    font-size: ${BRAND.font.size.hero};
    font-weight: ${BRAND.font.weight.black};
    line-height: ${BRAND.font.lineHeight.tight};
    letter-spacing: ${BRAND.font.letterSpacing.tight};
    margin-bottom: 16mm;
  }
  .portada-titulo .l1 { color: ${BRAND.color.paper}; display: block; }
  .portada-titulo .l2 { color: ${BRAND.color.primary}; display: block; }
  .portada-footer {
    border-top: 1px solid ${BRAND.color.dividerDark};
    padding-top: 6mm;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.wide};
  }

  /* ── Contraportada — fondo negro + cuadrado naranja espejado abajo izq ── */
  .contraportada {
    position: relative;
    width: 210mm;
    height: 297mm;
    background: ${BRAND.color.inkSoft};
    color: ${BRAND.color.paper};
    overflow: hidden;
    page-break-before: always;
  }
  .contraportada-logo {
    position: absolute;
    top: 22mm;
    left: 22mm;
    width: 50mm;
    height: auto;
    z-index: 1;
  }
  .contraportada-cuadrado {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50%;
    height: 60%;
    background: ${BRAND.color.primary};
    padding: 14mm 12mm;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .contraportada-cuadrado-top {
    display: flex;
    align-items: flex-start;
    gap: 8mm;
  }
  .contraportada-isologo {
    width: 40mm;
    height: 40mm;
    flex-shrink: 0;
    display: block;
  }
  .contraportada-tagline {
    font-size: ${BRAND.font.size.eyebrow};
    color: rgba(0, 0, 0, 0.55);
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-top: 6mm;
    line-height: ${BRAND.font.lineHeight.snug};
  }
  .contraportada-numero-propuesta {
    font-size: ${BRAND.font.size.eyebrow};
    color: rgba(0, 0, 0, 0.55);
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.bold};
  }
  .contraportada-qr {
    background: ${BRAND.color.paper};
    padding: 3mm;
    flex-shrink: 0;
  }
  .contraportada-qr svg {
    width: 32mm;
    height: 32mm;
    display: block;
  }
  .contraportada-qr-label {
    font-size: 6.5pt;
    color: rgba(0, 0, 0, 0.55);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2mm;
    text-align: center;
  }

  .contraportada-bloque-derecho {
    position: absolute;
    top: 22mm;
    right: 22mm;
    width: 95mm;
    bottom: 22mm;
    padding-left: 22mm;
    display: flex;
    flex-direction: column;
  }
  .contraportada-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-top: 26mm;
    margin-bottom: 6mm;
    text-align: right;
  }
  .contraportada-titulo {
    font-size: 36pt;
    font-weight: ${BRAND.font.weight.black};
    line-height: ${BRAND.font.lineHeight.tight};
    letter-spacing: ${BRAND.font.letterSpacing.tight};
    margin-bottom: 12mm;
    text-align: right;
  }
  .contraportada-titulo .l1 { color: ${BRAND.color.paper}; display: block; }
  .contraportada-titulo .l2 { color: ${BRAND.color.primary}; display: block; }

  .contraportada-pasos {
    list-style: none;
    padding: 0;
    margin: 0 0 10mm 0;
  }
  .contraportada-pasos li {
    margin-bottom: 6mm;
    text-align: right;
  }
  .contraportada-paso-numero {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.primary};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.bold};
    margin-bottom: 1mm;
  }
  .contraportada-paso-titulo {
    font-size: ${BRAND.font.size.bodyLarge};
    color: ${BRAND.color.paper};
    font-weight: ${BRAND.font.weight.bold};
    line-height: ${BRAND.font.lineHeight.snug};
  }
  .contraportada-paso-desc {
    font-size: ${BRAND.font.size.bodySmall};
    color: ${BRAND.color.grey50};
    line-height: ${BRAND.font.lineHeight.snug};
    margin-top: 1mm;
  }
  .contraportada-aviso {
    margin-bottom: 10mm;
    text-align: right;
  }
  .contraportada-aviso-eyebrow {
    display: block;
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.primary};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.bold};
    margin-bottom: 1mm;
  }
  .contraportada-aviso-desc {
    display: block;
    font-size: ${BRAND.font.size.bodySmall};
    color: ${BRAND.color.grey50};
    line-height: ${BRAND.font.lineHeight.snug};
  }
  .contraportada-contacto {
    margin-top: auto;
    text-align: right;
  }
  .contraportada-contacto-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-bottom: 2mm;
  }
  .contraportada-contacto-linea {
    font-size: ${BRAND.font.size.bodySmall};
    color: ${BRAND.color.paper};
    line-height: ${BRAND.font.lineHeight.snug};
  }

  /* ── Índice — sección dedicada con page-break-after para quedar sola.    */
  /* Reusa los estilos de .seccion-eyebrow / .seccion-titulo-row /          */
  /* .seccion-icono / .seccion-titulo / .seccion-divider para mantener      */
  /* coherencia visual con las secciones internas. Lista propia abajo.      */
  .indice-pagina { page-break-after: always; }
  .indice-lista {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8mm;
  }
  .indice-item {
    display: flex;
    align-items: baseline;
    gap: 8mm;
  }
  .indice-item .numero {
    flex: 0 0 auto;
    font-size: ${BRAND.font.size.eyebrow};
    font-weight: ${BRAND.font.weight.bold};
    color: ${BRAND.color.primary};
    letter-spacing: ${BRAND.font.letterSpacing.widest};
  }
  .indice-item .nombre {
    font-size: ${BRAND.font.size.body};
    font-weight: ${BRAND.font.weight.medium};
    color: ${BRAND.color.ink};
  }

  /* ── Sección interna — eyebrow + título + línea divisoria + body ── */
  .seccion-interna {
    break-before: page;
    margin-bottom: 14mm;
  }
  .seccion-interna:first-of-type { break-before: auto; }
  .seccion-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.primary};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-bottom: 6mm;
  }
  .seccion-titulo-row {
    display: flex;
    align-items: center;
    gap: 10pt;
    margin-bottom: 8mm;
  }
  .seccion-icono {
    display: inline-block;
    width: 18pt;
    height: 18pt;
    background: ${BRAND.color.primary};
    flex-shrink: 0;
  }
  .seccion-titulo {
    font-size: ${BRAND.font.size.h2};
    font-weight: ${BRAND.font.weight.black};
    color: ${BRAND.color.inkSoft};
    letter-spacing: ${BRAND.font.letterSpacing.tight};
    line-height: ${BRAND.font.lineHeight.snug};
    margin: 0;
  }
  .seccion-divider {
    border: 0;
    border-top: 1px solid ${BRAND.color.divider};
    margin: 0 0 10mm 0;
  }
  .seccion-body {
    font-size: ${BRAND.font.size.body};
    color: ${BRAND.color.inkSoft};
    line-height: ${BRAND.font.lineHeight.body};
  }
  .seccion-parrafo {
    margin: 0 0 12pt 0;
    text-indent: 0;
  }
  .seccion-parrafo.italica {
    font-style: italic;
    color: ${BRAND.color.grey70};
  }

  /* ── Fases — card con cuadrado numerado + eyebrows + bullets cuadrados ── */
  .empresa-block {
    margin-bottom: 12mm;
  }
  /* Con dos o más empresas cada una arranca en carilla propia: es la separación
     más fuerte posible y no agrega ningún material nuevo al documento. El
     modificador existe solo para este salto; el encabezado se ve igual con una
     empresa o con varias. */
  .empresa-block--multi + .empresa-block--multi {
    break-before: page;
  }
  .empresa-regla {
    height: 3pt;
    background: ${BRAND.color.primary};
    margin-bottom: 4mm;
  }
  .empresa-header {
    border-bottom: 1px solid ${BRAND.color.divider};
    padding-bottom: 4mm;
    margin-bottom: 8mm;
    page-break-after: avoid;
  }
  .empresa-eyebrow-row {
    display: flex;
    align-items: baseline;
    gap: 4mm;
    margin-bottom: 2mm;
  }
  .empresa-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
  }
  .empresa-servicios {
    /* Se mantiene a la derecha exista o no el contador de empresas. */
    margin-left: auto;
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.primary};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
  }
  .empresa-nombre-row {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 3mm;
  }
  .empresa-nombre {
    font-size: ${BRAND.font.size.h3};
    font-weight: ${BRAND.font.weight.bold};
    color: ${BRAND.color.inkSoft};
  }
  /* El RUT distingue dos sociedades que comparten razón social, que es el caso
     que obligó a agrupar por RUT y no por nombre. */
  .empresa-rut {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey70};
    letter-spacing: ${BRAND.font.letterSpacing.wide};
  }
  .fase-card {
    margin-bottom: 10mm;
  }
  .fase-card-header {
    display: flex;
    align-items: flex-start;
    gap: 4mm;
    margin-bottom: 6mm;
    break-inside: avoid;
    break-after: avoid;
  }
  .fase-numero {
    flex-shrink: 0;
    width: 32pt;
    height: 32pt;
    background: ${BRAND.color.primary};
    color: ${BRAND.color.paper};
    font-size: ${BRAND.font.size.bodyLarge};
    font-weight: ${BRAND.font.weight.black};
    display: flex;
    align-items: center;
    justify-content: center;
    letter-spacing: 0;
  }
  .fase-titulo-wrap {
    display: flex;
    flex-direction: column;
    gap: 1mm;
    padding-top: 2pt;
  }
  .fase-eyebrow-fase {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
  }
  .fase-titulo {
    font-size: ${BRAND.font.size.h3};
    font-weight: ${BRAND.font.weight.bold};
    color: ${BRAND.color.inkSoft};
    line-height: ${BRAND.font.lineHeight.snug};
  }
  .fase-caption {
    font-size: ${BRAND.font.size.bodySmall};
    font-weight: ${BRAND.font.weight.light};
    color: ${BRAND.color.grey70};
    line-height: ${BRAND.font.lineHeight.snug};
  }
  /* El header de la fase ya evita el salto hacia lo que sigue, pero la junta
     entre la línea divisoria y su eyebrow no la protegía nadie: el corte caía
     ahí y dejaba el título de la fase solo al pie de la página. */
  .fase-block-divider {
    border: 0;
    border-top: 1px solid ${BRAND.color.divider};
    margin: 4mm 0;
    break-before: avoid;
  }
  .fase-block-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.primary};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-bottom: 3mm;
    break-before: avoid;
    break-after: avoid;
  }
  .fase-block-body {
    font-size: ${BRAND.font.size.body};
    color: ${BRAND.color.inkSoft};
    line-height: ${BRAND.font.lineHeight.body};
  }
  .fase-actividades-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .fase-actividades-list li {
    position: relative;
    padding-left: 12pt;
    margin-bottom: 2mm;
    line-height: ${BRAND.font.lineHeight.body};
    break-inside: avoid;
  }
  .fase-actividades-list li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 5pt;
    height: 5pt;
    background: ${BRAND.color.primary};
  }
  .fase-footer {
    display: flex;
    gap: 14mm;
    flex-wrap: wrap;
    align-items: flex-end;
    border-top: 1px solid ${BRAND.color.divider};
    padding-top: 4mm;
    margin-top: 4mm;
    break-inside: avoid;
    break-before: avoid;
  }
  .fase-footer-item-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-bottom: 1mm;
  }
  .fase-footer-honorarios {
    font-size: ${BRAND.font.size.h3};
    font-weight: ${BRAND.font.weight.black};
    color: ${BRAND.color.primary};
    line-height: ${BRAND.font.lineHeight.tight};
  }
  .fase-footer-honorarios-original {
    font-size: ${BRAND.font.size.bodyLarge};
    font-weight: ${BRAND.font.weight.regular};
    color: ${BRAND.color.grey50};
    text-decoration: line-through;
    line-height: ${BRAND.font.lineHeight.tight};
    margin-bottom: 1mm;
  }
  .fase-footer-honorarios-modalidad {
    font-size: ${BRAND.font.size.bodySmall};
    font-weight: ${BRAND.font.weight.regular};
    color: ${BRAND.color.grey70};
    margin-top: 1mm;
  }
  .fase-footer-descuento-chip {
    display: inline-block;
    margin-top: 1.5mm;
    padding: 0.6mm 2mm;
    font-size: ${BRAND.font.size.eyebrow};
    font-weight: ${BRAND.font.weight.bold};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.wide};
    color: ${BRAND.color.primary};
    background: ${BRAND.color.primaryTint};
    border: 1px solid ${BRAND.color.primary};
    border-radius: 2mm;
  }
  .fase-footer-descuento-vence {
    margin-top: 1mm;
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey70};
    letter-spacing: ${BRAND.font.letterSpacing.wide};
  }
  .fase-footer-duracion {
    font-size: ${BRAND.font.size.bodyLarge};
    font-weight: ${BRAND.font.weight.medium};
    color: ${BRAND.color.inkSoft};
    line-height: ${BRAND.font.lineHeight.tight};
  }
  /* El subtotal no debe separarse de la última fase de su empresa: cuando el
     bloque no cabe entero, quedaba solo en una página con nada más que su línea
     de total, y la sección siguiente arrancaba en la posterior por su propio
     salto. break-inside lo mantiene íntegro y break-before avoid lo pega a lo
     que lo precede. */
  .subtotal-grupo {
    text-align: right;
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey70};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.wide};
    margin-top: 6mm;
    background: ${BRAND.color.grey10};
    padding: 3mm 4mm;
    break-inside: avoid;
    break-before: avoid;
  }
  .subtotal-grupo strong {
    color: ${BRAND.color.primary};
    font-weight: ${BRAND.font.weight.bold};
  }

  /* ── Equipo — avatar cuadrado 48×48 (foto o inicial naranja/negro) ── */
  .equipo-miembro {
    display: flex;
    gap: 6mm;
    align-items: flex-start;
    padding: 6mm 0;
    border-bottom: 1px solid ${BRAND.color.divider};
    break-inside: avoid;
  }
  .equipo-miembro:last-child { border-bottom: 0; }
  .equipo-avatar,
  .equipo-avatar-fallback {
    flex-shrink: 0;
    width: 48pt;
    height: 48pt;
  }
  .equipo-avatar { object-fit: cover; }
  .equipo-avatar-fallback {
    color: ${BRAND.color.paper};
    font-size: ${BRAND.font.size.h2};
    font-weight: ${BRAND.font.weight.black};
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .equipo-avatar-fallback.bg-primary { background: ${BRAND.color.primary}; }
  .equipo-avatar-fallback.bg-ink { background: ${BRAND.color.inkSoft}; }
  .equipo-info {
    display: flex;
    flex-direction: column;
    gap: 1mm;
    padding-top: 1mm;
  }
  .equipo-nombre {
    font-size: ${BRAND.font.size.bodyLarge};
    font-weight: ${BRAND.font.weight.black};
    color: ${BRAND.color.inkSoft};
    line-height: ${BRAND.font.lineHeight.tight};
  }
  .equipo-cargo {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.primary};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.bold};
  }
  .equipo-desc {
    font-size: ${BRAND.font.size.bodySmall};
    color: ${BRAND.color.grey70};
    line-height: ${BRAND.font.lineHeight.body};
    margin-top: 2mm;
  }

  /* ── Propuesta económica — cuadrado negro dominante + desglose lateral ── */
  .economica-bloque {
    display: flex;
    gap: 8mm;
    align-items: stretch;
    margin-bottom: 12mm;
    break-inside: avoid;
  }
  .economica-cuadrado {
    flex: 0 0 60mm;
    min-height: 90mm;
    background: ${BRAND.color.inkSoft};
    color: ${BRAND.color.paper};
    padding: 18mm 6mm 14mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  .economica-cuadrado.multi { padding: 12mm 6mm 14mm; }
  .economica-cuadrado-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-bottom: 6mm;
  }
  .economica-cuadrado-divider {
    width: 25%;
    border: 0;
    border-top: 1px solid ${BRAND.color.primary};
    margin: 0 0 8mm 0;
  }
  .economica-cuadrado-bloque-moneda {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .economica-cuadrado-bloque-moneda + .economica-cuadrado-bloque-moneda {
    margin-top: 6mm;
    padding-top: 6mm;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
  }
  .economica-cuadrado-monto {
    font-size: 20pt;
    font-weight: ${BRAND.font.weight.black};
    color: ${BRAND.color.paper};
    line-height: ${BRAND.font.lineHeight.tight};
    letter-spacing: ${BRAND.font.letterSpacing.tight};
    margin-bottom: 1mm;
  }
  .economica-cuadrado.multi .economica-cuadrado-monto { font-size: 17pt; }
  .economica-cuadrado-moneda {
    font-size: 10pt;
    font-weight: ${BRAND.font.weight.bold};
    color: ${BRAND.color.primary};
    letter-spacing: ${BRAND.font.letterSpacing.wide};
  }
  .economica-panel-derecho {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-top: 18mm;
  }
  .economica-panel-derecho.multi { padding-top: 12mm; }
  .economica-desglose {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  .economica-desglose-item {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 6mm;
    font-size: ${BRAND.font.size.body};
    color: ${BRAND.color.inkSoft};
    margin-bottom: 4mm;
  }
  .economica-desglose-item .nombre { flex: 1; }
  .economica-desglose-item .monto {
    font-weight: ${BRAND.font.weight.bold};
    white-space: nowrap;
  }
  .economica-desglose-item .moneda-label {
    color: ${BRAND.color.grey50};
    font-weight: ${BRAND.font.weight.regular};
    margin-left: 4pt;
  }
  .economica-cuadrado-modalidad {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.wide};
    font-weight: ${BRAND.font.weight.medium};
    margin-bottom: 1mm;
  }
  .economica-grupo-titulo {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.bold};
    margin: 6mm 0 3mm;
  }
  .economica-grupo-titulo:first-child { margin-top: 0; }
  .economica-desglose-item .modalidad {
    display: block;
    font-size: ${BRAND.font.size.bodySmall};
    color: ${BRAND.color.grey70};
    font-weight: ${BRAND.font.weight.regular};
    margin-top: 0.5mm;
  }
  .economica-nota {
    font-size: ${BRAND.font.size.bodySmall};
    color: ${BRAND.color.grey50};
    font-style: italic;
    margin-top: 6mm;
  }
  .economica-condiciones {
    margin-top: 50pt;
    break-inside: avoid;
  }
  .economica-condiciones-eyebrow {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-bottom: 4mm;
  }
  .economica-duracion {
    font-size: ${BRAND.font.size.body};
    color: ${BRAND.color.inkSoft};
    margin-top: 6mm;
  }
  .economica-duracion .label {
    font-size: ${BRAND.font.size.eyebrow};
    color: ${BRAND.color.grey50};
    text-transform: uppercase;
    letter-spacing: ${BRAND.font.letterSpacing.widest};
    font-weight: ${BRAND.font.weight.medium};
    margin-right: 4pt;
  }

  /* ── Screen-only: simula la metáfora de página A4 dentro de un iframe.
     En print mode (Puppeteer -> PDF) este bloque queda inactivo y la
     geometría la maneja @page + el width: 210mm de portada/contraportada.
     En screen mode (preview), el body se vuelve gris claro funcionando
     como gutter visible entre páginas; cada bloque queda centrado a
     210mm con padding equivalente al @page margin y un shadow sutil
     para reforzar la separación visual. */
  @media screen {
    body {
      background: ${BRAND.color.divider};
      padding: 8mm 0;
    }
    .portada,
    .contraportada,
    .indice-pagina,
    .seccion-interna {
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 8mm;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    .indice-pagina,
    .seccion-interna {
      width: 210mm;
      background: ${BRAND.color.paper};
      padding: 20mm 22mm;
      box-sizing: border-box;
    }
  }

  /* ── Próximos Pasos — lista ordenada con numeración grande a la izquierda ── */
  .proximos-pasos-dek {
    font-family: ${BRAND.font.family.base};
    font-size: ${BRAND.font.size.body};
    font-style: italic;
    color: ${BRAND.color.grey70};
    margin: 0 0 6mm 0;
  }

  .proximos-pasos-lista {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6mm;
  }

  .proximos-pasos-item {
    display: flex;
    gap: 6mm;
    align-items: flex-start;
    break-inside: avoid;
  }

  .proximos-pasos-numero {
    font-family: ${BRAND.font.family.base};
    font-weight: ${BRAND.font.weight.bold};
    font-size: 28pt;
    color: ${BRAND.color.primary};
    line-height: 1;
    flex-shrink: 0;
    min-width: 18mm;
  }

  .proximos-pasos-cuerpo {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2mm;
  }

  .proximos-pasos-titulo {
    font-family: ${BRAND.font.family.base};
    font-weight: ${BRAND.font.weight.bold};
    font-size: ${BRAND.font.size.bodyLarge};
    color: ${BRAND.color.inkSoft};
  }

  .proximos-pasos-descripcion {
    font-family: ${BRAND.font.family.base};
    font-weight: ${BRAND.font.weight.regular};
    font-size: ${BRAND.font.size.body};
    color: ${BRAND.color.grey70};
    line-height: ${BRAND.font.lineHeight.body};
  }

`;

// ─── Función principal ─────────────────────────────────────────────────────────

/**
 * Genera el HTML completo de una propuesta v2 para su conversión a PDF con
 * Puppeteer. La estructura y la lógica condicional son equivalentes a
 * ProposalPreview.js.
 *
 * @param {object} params
 * @param {object} params.propuesta  Campos textuales de la propuesta
 * @param {Array}  params.fases      Ítems de la propuesta (PLAN o TAREA)
 * @param {Array}  params.equipo     Miembros del equipo snapshot
 * @returns {string} HTML completo listo para page.setContent()
 */
// `mode` controla qué secciones se ensamblan en el body, sin duplicar cómputo:
//   - "completo" (default): documento entero (portada + índice + narrativas + contraportada)
//   - "portada-contraportada": solo portada y contraportada (full-bleed sin displayHeaderFooter)
//   - "indice": solo el índice (sin displayHeaderFooter ni paginación — su número de
//     sección coincide con la paginación de las narrativas en el merge)
//   - "internas": solo secciones narrativas (Quiénes Somos en adelante), con
//     displayHeaderFooter y paginación 1..N propia
// renderizarPDFCompleto hace 3 pasadas con los modos parciales y mergea con pdf-lib;
// el caller que renderiza HTML directo (preview) usa el modo completo.
export function buildProposalHTMLv2({
  propuesta = {},
  fases = [],
  equipo = [],
  qrPortalSVG = null,
  mode = "completo",
}) {
  const p = propuesta;
  const f = Array.isArray(fases) ? fases : [];
  const e = Array.isArray(equipo) ? equipo : [];

  const tieneFases = f.some((fase) => fase.titulo || fase.objetivo);
  const tieneEquipo = e.length > 0;

  // ── Numeración dinámica de secciones ──
  let seccion = 0;
  const ns = () => ++seccion;

  // ── Agrupación de fases por empresa ──
  // Map y no objeto plano: las claves de empresa son RUT sin puntos ni guion, y
  // un string de dígitos menor que 2^32-1 es índice de array para el motor, así
  // que Object.values() lo devolvería en orden numérico ascendente en vez del
  // orden de inserción. Eso reordenaba los grupos respecto del orden con que se
  // asignó el número de fase, y la primera empresa del PDF quedaba con las
  // fases 04-06 y la segunda con las 01-03. Map preserva el orden de inserción
  // sea cual sea el tipo de clave.
  const gruposEmpresas = [
    ...f
      .reduce((acc, fase) => {
        const key = claveEmpresaFase(fase);
        if (!acc.has(key)) {
          acc.set(key, {
            key,
            nombre: fase.nombreCliente || fase.refCliente || null,
            rut: fase.refCliente || null,
            fases: [],
          });
        }
        acc.get(key).fases.push(fase);
        return acc;
      }, new Map())
      .values(),
  ];
  const mostrarEmpresa =
    gruposEmpresas.length > 1 ||
    (gruposEmpresas.length === 1 && !!gruposEmpresas[0].nombre);
  // El encabezado de empresa se ve igual con una o con varias; lo único que
  // depende de esto es el contador "Empresa 1 de 2" y el salto de carilla.
  const multiEmpresa = gruposEmpresas.length > 1;

  // ── Total general por moneda ──
  const totalPorMoneda = f.reduce((acc, fase) => {
    if (!fase.monto) return acc;
    const moneda = formatearMonedaCliente(fase.tipoMoneda) || "CLP";
    acc[moneda] = (acc[moneda] || 0) + Number(fase.monto);
    return acc;
  }, {});
  const lineasTotal = Object.entries(totalPorMoneda)
    .map(([m, s]) => formatValorPlan(s, m, { estilo: "v2", emptyAs: null }))
    .filter(Boolean);

  const proximosPasosParsed = parseProximosPasos(p.proximosPasos);
  const tieneProximosPasos =
    proximosPasosParsed.estructurado || Boolean(proximosPasosParsed.textoBruto);
  const cuerpoProximosPasos = proximosPasosParsed.estructurado
    ? renderProximosPasos(proximosPasosParsed.bloques)
    : renderProximosPasosProsa(proximosPasosParsed.textoBruto);

  // ── Índice de contenidos ──
  const itemsIndice = [
    p.introduccion && "Quiénes Somos",
    p.resumenEjecutivo && "Resumen Ejecutivo",
    (p.antecedentes || p.descripcion) && "Antecedentes",
    p.alcance && "Alcance del Servicio",
    tieneFases && "Metodología de Trabajo",
    tieneEquipo && "Equipo de Trabajo",
    (lineasTotal.length > 0 || p.condicionesPago) && "Propuesta Económica",
    tieneProximosPasos && "Próximos Pasos",
    p.clausulaConfidencialidad && "Confidencialidad",
  ].filter(Boolean);

  // ─────────────────────────────────────────────────────────────────────────────
  // SECCIONES
  // ─────────────────────────────────────────────────────────────────────────────

  // ── Portada ──
  const tituloPortada = p.titulo || "Propuesta de Servicios";
  const { linea1: tituloL1, linea2: tituloL2 } =
    splitTituloBicolor(tituloPortada);
  const fechaDocumento =
    p.fechaEnvioCorreo || new Date().toISOString().slice(0, 10);
  const anioPortada = obtenerAnio(fechaDocumento);

  const htmlPortada = `
    <section class="portada">
      <div class="portada-cuadrado"></div>
      <img class="portada-logo" src="${LOGO_PRINCIPAL_BLANCO_SRC}" alt="TAXTIC" />
      <div class="portada-bloque-inferior">
        <div class="portada-eyebrow">Propuesta · ${esc(anioPortada)}</div>
        <div class="portada-titulo">
          <span class="l1">${esc(tituloL1)}</span>
          ${tituloL2 ? `<span class="l2">${esc(tituloL2)}</span>` : ""}
        </div>
        <div class="portada-footer">
          <span>${esc(p.nombreSolicitante || "")}</span>
          <span>${esc(formatFechaPortada(fechaDocumento))}</span>
        </div>
      </div>
    </section>
  `;

  // ── Índice ──
  // Envuelto en <section class="indice-pagina"> para dos cosas: (1) garantizar
  // que el índice queda solo en su propia página vía page-break-after, y (2)
  // que sea el primer <section> del DOM en modo "internas", para que la regla
  // `.seccion-interna:first-of-type { break-before: auto }` no matchee la
  // primera sección narrativa y todas conserven su page-break.
  // Numeración 01..NN: orden secuencial de la sección, no número de página
  // física. Las secciones cuyos sub-elementos llevan `break-inside: avoid`
  // (p.ej. `.fase-card-header` o `.fase-footer`; la tarjeta entera no lo
  // lleva, a propósito) pueden expandirse a varias páginas en el PDF C, por
  // lo que el ítem N del índice no corresponde necesariamente con la página
  // N del footer.
  const htmlIndice =
    itemsIndice.length > 0
      ? `
    <section class="indice-pagina">
      <div class="seccion-eyebrow">Contenidos</div>
      <div class="seccion-titulo-row">
        <span class="seccion-icono"></span>
        <h2 class="seccion-titulo">Contenidos</h2>
      </div>
      <hr class="seccion-divider" />
      <ol class="indice-lista">
        ${itemsIndice
          .map(
            (item, i) => `
          <li class="indice-item">
            <span class="numero">${esc(padNumero(i + 1))}</span>
            <span class="nombre">${esc(item)}</span>
          </li>
        `,
          )
          .join("")}
      </ol>
    </section>
  `
      : "";

  // ── Quiénes somos ──
  const htmlQuienesSomos = p.introduccion
    ? renderSeccionInternaV2({
        numero: ns(),
        titulo: "Quiénes Somos",
        body: renderParrafosV2(p.introduccion),
      })
    : "";

  // ── Resumen Ejecutivo ──
  const htmlResumen = p.resumenEjecutivo
    ? renderSeccionInternaV2({
        numero: ns(),
        titulo: "Resumen Ejecutivo",
        body: renderParrafosV2(p.resumenEjecutivo),
      })
    : "";

  // ── Antecedentes ──
  const htmlAntecedentes =
    p.antecedentes || p.descripcion
      ? renderSeccionInternaV2({
          numero: ns(),
          titulo: "Antecedentes",
          body: `
          ${p.descripcion ? `<p class="seccion-parrafo italica">${esc(p.descripcion)}</p>` : ""}
          ${renderParrafosV2(p.antecedentes)}
        `,
        })
      : "";

  // ── Alcance ──
  const htmlAlcance = p.alcance
    ? renderSeccionInternaV2({
        numero: ns(),
        titulo: "Alcance del Servicio",
        body: renderParrafosV2(p.alcance),
      })
    : "";

  // ── Metodología (fases agrupadas por empresa) ──
  const htmlFases = tieneFases
    ? renderSeccionInternaV2({
        numero: ns(),
        titulo: "Metodología de Trabajo",
        body: `
    ${gruposEmpresas
      .map((grupo, gi) => {
        // Los ítems que se cobran por trabajador no comparten base de cálculo
        // con los de tarifa fija: sumarlos daría un total que no corresponde a
        // ningún monto real. Se acumulan aparte y salen como su propia línea.
        const subtotalGrupo = grupo.fases.reduce((acc, fase) => {
          if (!fase.monto) return acc;
          const bucket =
            fase.tipoItem === "PLAN" && esCobroPorTrabajador(fase.refTipoPlan)
              ? "porTrabajador"
              : "fijo";
          const moneda = formatearMonedaCliente(fase.tipoMoneda) || "CLP";
          acc[bucket] = acc[bucket] || {};
          acc[bucket][moneda] = (acc[bucket][moneda] || 0) + Number(fase.monto);
          return acc;
        }, {});
        const lineasSubtotalFijo = Object.entries(subtotalGrupo.fijo || {})
          .map(([m, s]) =>
            formatValorPlan(s, m, { estilo: "v2", emptyAs: null }),
          )
          .filter(Boolean);
        const lineasSubtotalPorTrabajador = Object.entries(
          subtotalGrupo.porTrabajador || {},
        )
          .map(([m, s]) =>
            formatValorPlan(s, m, { estilo: "v2", emptyAs: null }),
          )
          .filter(Boolean)
          .map((txt) => `${txt} por trabajador`);
        const lineasSubtotal = [
          ...lineasSubtotalFijo,
          ...lineasSubtotalPorTrabajador,
        ];

        // Sin razón social el título ya era el RUT: se imprime formateado y no
        // se repite al costado.
        const rutFmt = grupo.rut
          ? formatRut(grupo.rut, RutFormat.DOTS_DASH)
          : "";
        const nombreEmpresa =
          grupo.nombre && grupo.nombre !== grupo.rut ? grupo.nombre : rutFmt;
        // Un cliente creado sin RUT lleva un identificador interno que no
        // puede salir en el documento que ve el cliente.
        const rutAlLado =
          isRutLike(grupo.rut) && rutFmt !== nombreEmpresa ? rutFmt : "";
        const nServicios = grupo.fases.length;

        return `
        <div class="empresa-block${multiEmpresa ? " empresa-block--multi" : ""}">
          ${
            mostrarEmpresa && nombreEmpresa
              ? `
            <div class="empresa-regla"></div>
            <div class="empresa-header">
              <div class="empresa-eyebrow-row">
                ${multiEmpresa ? `<span class="empresa-eyebrow">Empresa ${gi + 1} de ${gruposEmpresas.length}</span>` : ""}
                <span class="empresa-servicios">${nServicios} ${nServicios === 1 ? "servicio" : "servicios"}</span>
              </div>
              <div class="empresa-nombre-row">
                <span class="empresa-nombre">${esc(nombreEmpresa)}</span>
                ${rutAlLado ? `<span class="empresa-rut">${esc(rutAlLado)}</span>` : ""}
              </div>
            </div>`
              : ""
          }
          ${grupo.fases
            .map((fase, i) => {
              const faseNum = fase.numero ?? i + 1;
              const actividades = (fase.actividades || "")
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean);
              const monedaCliente = formatearMonedaCliente(fase.tipoMoneda);
              const honorariosTxt =
                fase.monto > 0
                  ? formatValorPlan(fase.monto, monedaCliente, {
                      estilo: "v2",
                      emptyAs: null,
                    })
                  : "";
              // Descuento explícito: mostrar valor original tachado, chip de
              // porcentaje y fecha de vencimiento si aplica. Sólo cuando hay
              // monto original distinto del final y un descuento real registrado.
              const descuentoNum = Number(fase.descuento) || 0;
              const montoOriginalNum = Number(fase.montoOriginal) || 0;
              const tieneDescuento =
                descuentoNum > 0 &&
                montoOriginalNum > 0 &&
                montoOriginalNum > Number(fase.monto);
              const honorariosOriginalTxt = tieneDescuento
                ? formatValorPlan(montoOriginalNum, monedaCliente, {
                    estilo: "v2",
                    emptyAs: null,
                  })
                : "";
              const descuentoChipTxt = tieneDescuento
                ? `-${descuentoNum % 1 === 0 ? descuentoNum : descuentoNum.toFixed(2).replace(/\.?0+$/, "")}% off`
                : "";
              const venceTxt =
                tieneDescuento && fase.fechaTerminoDesc
                  ? `Vence ${formatFechaPortada(fase.fechaTerminoDesc)}`
                  : "";
              const modClave = modalidadItem(fase);
              const modalidadInlineTxt = MODALIDAD_META[modClave]?.inline || "";
              const esTarea = fase.tipoItem !== "PLAN";
              const fechaEntregaTxt =
                esTarea && fase.fechaLimite
                  ? formatFechaPortada(fase.fechaLimite)
                  : "";
              const tieneFooter =
                honorariosTxt || fase.duracion || fechaEntregaTxt;

              return `
              <div class="fase-card">
                <div class="fase-card-header">
                  <div class="fase-numero">${esc(padNumero(faseNum))}</div>
                  <div class="fase-titulo-wrap">
                    <span class="fase-eyebrow-fase">Fase ${esc(padNumero(faseNum))}</span>
                    ${fase.titulo ? `<span class="fase-titulo">${esc(fase.titulo)}</span>` : ""}
                    ${fase.tipoCaption ? `<span class="fase-caption">${esc(fase.tipoCaption)}</span>` : ""}
                  </div>
                </div>
                ${
                  fase.objetivo
                    ? `
                  <hr class="fase-block-divider" />
                  <div class="fase-block-eyebrow">Objetivo</div>
                  <div class="fase-block-body">${esc(fase.objetivo)}</div>
                `
                    : ""
                }
                ${
                  actividades.length > 0
                    ? `
                  <hr class="fase-block-divider" />
                  <div class="fase-block-eyebrow">Actividades</div>
                  <ul class="fase-actividades-list">
                    ${actividades.map((act) => `<li>${esc(act)}</li>`).join("")}
                  </ul>
                `
                    : ""
                }
                ${
                  fase.entregable
                    ? `
                  <hr class="fase-block-divider" />
                  <div class="fase-block-eyebrow">Entregable</div>
                  <div class="fase-block-body">${esc(fase.entregable)}</div>
                `
                    : ""
                }
                ${
                  tieneFooter
                    ? `
                  <div class="fase-footer">
                    ${
                      honorariosTxt
                        ? `
                      <div>
                        <div class="fase-footer-item-eyebrow">Honorarios</div>
                        ${honorariosOriginalTxt ? `<div class="fase-footer-honorarios-original">${esc(honorariosOriginalTxt)}</div>` : ""}
                        <div class="fase-footer-honorarios">${esc(honorariosTxt)}</div>
                        ${modalidadInlineTxt ? `<div class="fase-footer-honorarios-modalidad">${esc(modalidadInlineTxt)}</div>` : ""}
                        ${descuentoChipTxt ? `<div class="fase-footer-descuento-chip">${esc(descuentoChipTxt)}</div>` : ""}
                        ${venceTxt ? `<div class="fase-footer-descuento-vence">${esc(venceTxt)}</div>` : ""}
                      </div>
                    `
                        : ""
                    }
                    ${
                      fechaEntregaTxt
                        ? `
                      <div>
                        <div class="fase-footer-item-eyebrow">Fecha de entrega</div>
                        <div class="fase-footer-duracion">${esc(fechaEntregaTxt)}</div>
                      </div>
                    `
                        : `${
                            fase.duracion
                              ? `
                      <div>
                        <div class="fase-footer-item-eyebrow">Duración</div>
                        <div class="fase-footer-duracion">${esc(fase.duracion)}</div>
                      </div>
                    `
                              : ""
                          }`
                    }
                  </div>
                `
                    : ""
                }
              </div>
            `;
            })
            .join("")}
          ${
            mostrarEmpresa && lineasSubtotal.length > 0
              ? `
            <div class="subtotal-grupo">
              Subtotal${grupo.nombre ? ` ${esc(grupo.nombre)}` : ""} <strong>${esc(lineasSubtotal.join(" + "))}</strong>
            </div>
          `
              : ""
          }
        </div>
      `;
      })
      .join("")}
    `,
      })
    : "";

  // ── Equipo de trabajo ──
  const htmlEquipo = tieneEquipo
    ? renderSeccionInternaV2({
        numero: ns(),
        titulo: "Equipo de Trabajo",
        body: `
    ${e
      .map((miembro, idx) => {
        const inicial = (miembro.nombre || "?").trim()[0]?.toUpperCase() ?? "?";
        const bgClass = idx % 2 === 0 ? "bg-primary" : "bg-ink";
        return `
      <div class="equipo-miembro">
        ${
          miembro.fotoUrl
            ? `<img class="equipo-avatar" src="${esc(miembro.fotoUrl)}" alt="${esc(miembro.nombre || "")}" />`
            : `<div class="equipo-avatar-fallback ${bgClass}">${esc(inicial)}</div>`
        }
        <div class="equipo-info">
          <div class="equipo-nombre">${esc(miembro.nombre || "Sin nombre")}</div>
          ${miembro.rolDisplay ? `<div class="equipo-cargo">${esc(miembro.rolDisplay)}</div>` : ""}
          ${miembro.descripcionExperiencia ? `<div class="equipo-desc">${esc(miembro.descripcionExperiencia)}</div>` : ""}
        </div>
      </div>
    `;
      })
      .join("")}
    `,
      })
    : "";

  // ── Propuesta económica ──
  // Cuadrado negro dominante con monto + moneda; desglose lateral con cada ítem;
  // condiciones de pago + duración como bloques separados.
  // Fases facturables agrupadas por modalidad de cobro.
  const fasesConMonto = f.filter((fase) => Number(fase.monto) > 0);

  // Subtotales por modalidad -> moneda.
  const totalPorModalidad = fasesConMonto.reduce((acc, fase) => {
    const clave = modalidadItem(fase);
    const moneda = formatearMonedaCliente(fase.tipoMoneda) || "CLP";
    acc[clave] = acc[clave] || {};
    acc[clave][moneda] = (acc[clave][moneda] || 0) + Number(fase.monto);
    return acc;
  }, {});

  const modalidadesPresentes = ORDEN_MODALIDAD.filter(
    (k) => totalPorModalidad[k],
  );
  const hayMontoEconomica = modalidadesPresentes.length > 0;
  const lineasCuadrado = modalidadesPresentes.reduce(
    (n, k) => n + Object.keys(totalPorModalidad[k]).length,
    0,
  );
  const esMulti = lineasCuadrado > 1;
  const mostrarNotaModalidad = modalidadesPresentes.length >= 2;

  // Cuadrado negro: una línea por modalidad+moneda, con etiqueta + monto + unidad.
  const cuadradoBloques = modalidadesPresentes
    .flatMap((clave) =>
      Object.entries(totalPorModalidad[clave]).map(([moneda, suma]) => {
        const txt = formatValorPlan(suma, moneda, {
          estilo: "v2",
          emptyAs: null,
        });
        const { monto: montoFmt, moneda: monedaFmt } = splitMontoMoneda(txt);
        const meta = MODALIDAD_META[clave];
        const unidad = meta.unidad ? ` ${meta.unidad}` : "";
        return `
        <div class="economica-cuadrado-bloque-moneda">
          <div class="economica-cuadrado-modalidad">${esc(meta.subtotal)}</div>
          <div class="economica-cuadrado-monto">${esc(montoFmt)}</div>
          <div class="economica-cuadrado-moneda">${esc((monedaFmt || moneda) + unidad)}</div>
        </div>
      `;
      }),
    )
    .join("");

  // Desglose agrupado en "Servicios recurrentes" y "Pago único".
  const GRUPOS_DESGLOSE = [
    { grupo: "RECURRENTE", titulo: "Servicios recurrentes" },
    { grupo: "UNICO", titulo: "Pago único" },
  ];
  const desgloseHTML = GRUPOS_DESGLOSE.map(({ grupo, titulo }) => {
    const fasesGrupo = fasesConMonto.filter(
      (fase) => MODALIDAD_META[modalidadItem(fase)].grupo === grupo,
    );
    if (fasesGrupo.length === 0) return "";
    const filas = fasesGrupo
      .map((fase) => {
        const moneda = formatearMonedaCliente(fase.tipoMoneda) || "CLP";
        const txt = formatValorPlan(Number(fase.monto), moneda, {
          estilo: "v2",
          emptyAs: null,
        });
        const { monto: montoFmt, moneda: monedaFmt } = splitMontoMoneda(txt);
        const inline = MODALIDAD_META[modalidadItem(fase)].inline;
        const nombre =
          fase.titulo || `Fase ${padNumero(fase.numero ?? "")}`.trim();
        return `
        <div class="economica-desglose-item">
          <span class="nombre">${esc(nombre)}</span>
          <span class="monto">${esc(montoFmt)}${monedaFmt ? `<span class="moneda-label">${esc(monedaFmt)}</span>` : ""}${inline ? `<span class="modalidad">${esc(inline)}</span>` : ""}</span>
        </div>
      `;
      })
      .join("");
    return `<div class="economica-grupo-titulo">${esc(titulo)}</div>${filas}`;
  }).join("");

  const htmlEconomica =
    hayMontoEconomica || p.condicionesPago || p.duracionEstimada
      ? renderSeccionInternaV2({
          numero: ns(),
          titulo: "Propuesta Económica",
          body: `
        ${
          hayMontoEconomica
            ? `
          <div class="economica-bloque">
            <div class="economica-cuadrado${esMulti ? " multi" : ""}">
              <div class="economica-cuadrado-eyebrow">Inversión Total</div>
              <hr class="economica-cuadrado-divider" />
              ${cuadradoBloques}
            </div>
            <div class="economica-panel-derecho${esMulti ? " multi" : ""}">
              <div class="economica-desglose">${desgloseHTML}</div>
              ${
                p.condicionesPago
                  ? `
                <div class="economica-condiciones">
                  <div class="economica-condiciones-eyebrow">Condiciones de Pago</div>
                  <div class="seccion-body">${renderParrafosV2(p.condicionesPago)}</div>
                </div>
              `
                  : ""
              }
              ${
                p.duracionEstimada
                  ? `
                <div class="economica-duracion">
                  <span class="label">Duración estimada</span>${esc(p.duracionEstimada)}
                </div>
              `
                  : ""
              }
              ${
                mostrarNotaModalidad
                  ? `
                <div class="economica-nota">Los montos mensuales, anuales y de pago único se muestran por separado según su modalidad de cobro.</div>
              `
                  : ""
              }
            </div>
          </div>
        `
            : ""
        }
        ${
          !hayMontoEconomica && p.condicionesPago
            ? `
          <div class="economica-condiciones">
            <div class="economica-condiciones-eyebrow">Condiciones de Pago</div>
            <div class="seccion-body">${renderParrafosV2(p.condicionesPago)}</div>
          </div>
        `
            : ""
        }
        ${
          !hayMontoEconomica && p.duracionEstimada
            ? `
          <div class="economica-duracion">
            <span class="label">Duración estimada</span>${esc(p.duracionEstimada)}
          </div>
        `
            : ""
        }
      `,
        })
      : "";

  // ── Próximos Pasos ──
  const htmlProximosPasos = tieneProximosPasos
    ? renderSeccionInternaV2({
        numero: ns(),
        titulo: "Próximos Pasos",
        body: `
        <p class="proximos-pasos-dek">Acciones que ejecutaremos tras la aceptación de la propuesta.</p>
        ${cuerpoProximosPasos}
      `,
      })
    : "";

  // ── Confidencialidad ──
  const htmlConfidencialidad = p.clausulaConfidencialidad
    ? renderSeccionInternaV2({
        numero: ns(),
        titulo: "Confidencialidad",
        body: renderParrafosV2(p.clausulaConfidencialidad),
      })
    : "";

  // ── Contraportada — pasos de aceptación fijos + cierre en página final ──
  const pasos = PASOS_ACEPTACION_DEFAULT;
  const tieneContacto = p.vendedorCorreo || p.vendedorTelefono;

  const htmlContraportada = `
    <section class="contraportada">
      <img class="contraportada-logo" src="${LOGO_PRINCIPAL_BLANCO_SRC}" alt="TAXTIC" />

      <div class="contraportada-cuadrado">
        <div class="contraportada-cuadrado-top">
          <img class="contraportada-isologo" src="${LOGO_ISOLOGO_NEGRO_SRC}" alt="TAXTIC" />
          ${
            qrPortalSVG
              ? `
            <div>
              <div class="contraportada-qr">${qrPortalSVG}</div>
              <div class="contraportada-qr-label">Escanee para acceder al portal</div>
            </div>
          `
              : ""
          }
        </div>
        <div>
          <div class="contraportada-tagline">Asesoría Tributaria Integral</div>
          <div class="contraportada-numero-propuesta" style="margin-top: 2mm;">Propuesta #${esc(String(p.id ?? ""))}</div>
        </div>
      </div>

      <div class="contraportada-bloque-derecho">
        <div class="contraportada-eyebrow">Cómo Aceptar</div>
        <div class="contraportada-titulo">
          <span class="l1">Listos para</span>
          <span class="l2">comenzar.</span>
        </div>

        <ol class="contraportada-pasos">
          ${pasos
            .map(
              (paso, i) => `
            <li>
              <div class="contraportada-paso-numero">${esc(padNumero(i + 1))}</div>
              <div class="contraportada-paso-titulo">${esc(paso.titulo)}</div>
              ${paso.descripcion ? `<div class="contraportada-paso-desc">${esc(paso.descripcion)}</div>` : ""}
            </li>
          `,
            )
            .join("")}
        </ol>

        <div class="contraportada-aviso">
          <span class="contraportada-aviso-eyebrow">Puede aceptar parcial</span>
          <span class="contraportada-aviso-desc">Acepte todos los servicios o solo aquellos que le interesen.</span>
        </div>

        ${
          tieneContacto
            ? `
          <div class="contraportada-contacto">
            <div class="contraportada-contacto-eyebrow">Contacto</div>
            ${p.vendedorCorreo ? `<div class="contraportada-contacto-linea">${esc(p.vendedorCorreo)}</div>` : ""}
            ${p.vendedorTelefono ? `<div class="contraportada-contacto-linea">${esc(p.vendedorTelefono)}</div>` : ""}
          </div>
        `
            : ""
        }
      </div>
    </section>
  `;

  // ─────────────────────────────────────────────────────────────────────────────
  // DOCUMENTO COMPLETO
  // ─────────────────────────────────────────────────────────────────────────────

  const seccionesNarrativas = `
  ${htmlQuienesSomos}
  ${htmlResumen}
  ${htmlAntecedentes}
  ${htmlAlcance}
  ${htmlFases}
  ${htmlEquipo}
  ${htmlEconomica}
  ${htmlProximosPasos}
  ${htmlConfidencialidad}`;

  const bodyContent =
    mode === "portada-contraportada"
      ? `${htmlPortada}\n  ${htmlContraportada}`
      : mode === "indice"
        ? htmlIndice
        : mode === "internas"
          ? seccionesNarrativas
          : `${htmlPortada}\n  ${htmlIndice}${seccionesNarrativas}\n  ${htmlContraportada}`;

  // Override del @page según el modo:
  //  - "portada-contraportada": forzar margin: 0 en TODAS las páginas (las 2
  //    son full-bleed). Chromium con preferCSSPageSize aplica @page :first/:last
  //    de forma inconsistente, así que sobrescribimos el @page general directo.
  //  - "indice" e "internas": la primera y última son secciones normales (no
  //    portadas físicas), así que neutralizamos el `margin: 0` de :first/:last
  //    del CSS general — si no, quedarían sin espacio para el header/footer
  //    corporativo (en internas) o sin margen visual (en índice).
  const _normalMargin = `${PDF_PAGE_GEOMETRY.marginTop} ${PDF_PAGE_GEOMETRY.marginRight} ${PDF_PAGE_GEOMETRY.marginBottom} ${PDF_PAGE_GEOMETRY.marginLeft}`;
  const cssOverride =
    mode === "portada-contraportada"
      ? `@page { margin: 0; }`
      : mode === "internas" || mode === "indice"
        ? `@page :first { margin: ${_normalMargin}; }
       @page :last  { margin: ${_normalMargin}; }`
        : "";

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${esc(p.titulo || "Propuesta de Servicios")}</title>
  <style>${CSS}${cssOverride}</style>
</head>
<body>${bodyContent}
</body>
</html>`;
}
